/**
 * Sequential multi-card checkout state derived from LDS order.payments[] (SSOT)
 * plus the planned card count/amounts in the UI.
 */
import { agorotToIls, ilsToAgorot } from "../publicMoneyAgorot";
import {
  parseLdsOrderPayments,
  resolveLdsPaymentSlotCapturedStrict,
} from "../orders/ldsSplitPaymentsSsot";
import { isLdsSplitPaymentSlotLocked } from "../orders/ldsSplitPaymentSlotLock";
import { resolveLdsFinancialState } from "../orders/resolveLdsFinancialState";
import { clampSequentialCardCount } from "./splitOrderAmountAcrossCards";

export const SEQ_STATUS = {
  PAID: "PAID",
  ACTIVE: "ACTIVE",
  PENDING: "PENDING",
  FAILED: "FAILED",
  PROCESSING: "PROCESSING",
};

const FAILED_STATUSES = new Set([
  "FAILED",
  "FAIL",
  "ERROR",
  "DECLINED",
  "REJECTED",
  "CANCELLED",
  "CANCELED",
]);

function truthyPaid(raw) {
  return raw === true || raw === "true" || raw === 1 || raw === "1";
}

function toNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function formatSequentialIls(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "₪0";
  const cents = Math.round(n * 100);
  const fractionDigits = cents % 100 === 0 ? 0 : 2;
  return `₪${n.toLocaleString("he-IL", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: 2,
  })}`;
}

export function isLdsPaymentSlotFailed(payment) {
  if (!payment || typeof payment !== "object") return false;
  if (resolveLdsPaymentSlotCapturedStrict(payment) > 0) return false;
  if (truthyPaid(payment.paid ?? payment.Paid)) return false;

  const status = String(payment.status ?? payment.Status ?? "")
    .trim()
    .toUpperCase();
  if (FAILED_STATUSES.has(status)) return true;

  if (payment.failed === true) return true;
  const err = payment.error ?? payment.error_message ?? payment.errorMessage;
  if (err != null && String(err).trim() !== "") return true;

  const ccode = String(
    payment.CCode ?? payment.ccode ?? payment.response_code ?? payment.responseCode ?? ""
  ).trim();
  if (!ccode) return false;
  if (ccode === "0" || ccode === "000") return false;
  return true;
}

export function isLdsPaymentSlotPaid(payment, order = null, index = 0) {
  if (!payment || typeof payment !== "object") return false;
  if (isLdsSplitPaymentSlotLocked(payment, order, index)) return true;
  if (resolveLdsPaymentSlotCapturedStrict(payment, order, index) > 0) return true;
  return truthyPaid(payment.paid ?? payment.Paid);
}

export function resolvePaymentSlotFromOrder(order, cardIndex, paymentLink = null) {
  const payments = parseLdsOrderPayments(order);
  if (Number.isInteger(cardIndex) && cardIndex >= 0 && payments[cardIndex]) {
    return payments[cardIndex];
  }
  if (!paymentLink) return null;
  return (
    payments.find(
      (p) =>
        Number(p?.amount) === Number(paymentLink.amount) &&
        Number(p?.tash) === Number(paymentLink.tash)
    ) || null
  );
}

function maskedLast4(payment) {
  if (!payment || typeof payment !== "object") return null;
  const raw =
    payment.last4 ??
    payment.card_last4 ??
    payment.cardLast4 ??
    payment.nLast4 ??
    payment.four_digits ??
    payment.cardMask ??
    null;
  if (raw == null) return null;
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length < 4) return null;
  return digits.slice(-4);
}

function slotMeta(payment) {
  if (!payment || typeof payment !== "object") {
    return {
      paymentId: null,
      provider: null,
      timestamp: null,
      last4: null,
      tash: null,
    };
  }
  return {
    paymentId:
      payment.id ??
      payment.payment_id ??
      payment.paymentId ??
      payment.transaction_id ??
      payment.transactionId ??
      payment.hyp_trans_id ??
      null,
    provider:
      payment.payment_provider ??
      payment.paymentProvider ??
      payment.provider ??
      payment.source ??
      null,
    timestamp:
      payment.callback_received_at ??
      payment.callbackReceivedAt ??
      payment.updated_at ??
      payment.updatedAt ??
      payment.created_at ??
      payment.createdAt ??
      null,
    last4: maskedLast4(payment),
    tash: payment.tash ?? payment.numberofpayments ?? null,
  };
}

export function resolveSequentialPaymentSteps({
  cardCount,
  plannedAmounts = [],
  order = null,
  processingIndex = null,
  localFailedIndex = null,
} = {}) {
  const n = clampSequentialCardCount(cardCount);
  const payments = parseLdsOrderPayments(order);
  const steps = [];
  let firstUnpaid = -1;

  for (let i = 0; i < n; i += 1) {
    const payment = payments[i] || null;
    const captured = payment
      ? resolveLdsPaymentSlotCapturedStrict(payment, order, i)
      : 0;
    const paid = isLdsPaymentSlotPaid(payment, order, i);
    const planned = toNum(plannedAmounts[i]);
    let status;

    if (paid) {
      status = SEQ_STATUS.PAID;
    } else if (firstUnpaid < 0) {
      firstUnpaid = i;
      if (processingIndex === i) status = SEQ_STATUS.PROCESSING;
      else if (localFailedIndex === i || isLdsPaymentSlotFailed(payment)) {
        status = SEQ_STATUS.FAILED;
      } else status = SEQ_STATUS.ACTIVE;
    } else {
      status = SEQ_STATUS.PENDING;
    }

    steps.push({
      index: i,
      status,
      plannedAmount: planned,
      chargedAmount: paid ? captured || toNum(payment?.actualAmount) : 0,
      expectedAmount: toNum(payment?.actualAmount ?? payment?.amount) || planned,
      ...slotMeta(payment),
    });
  }

  const allPaid = firstUnpaid < 0 && n > 0 && steps.every((s) => s.status === SEQ_STATUS.PAID);
  const activeIndex = firstUnpaid < 0 ? Math.max(0, n - 1) : firstUnpaid;

  return {
    steps,
    activeIndex,
    allPaid,
    paidCount: steps.filter((s) => s.status === SEQ_STATUS.PAID).length,
  };
}

export function resolveSequentialTotals({ order, payableIls, sumpaidFallback = 0 } = {}) {
  const fin = order ? resolveLdsFinancialState(order) : null;
  const checkoutPayable = Number(payableIls);
  const required =
    Number.isFinite(checkoutPayable) && checkoutPayable > 0
      ? checkoutPayable
      : fin?.totalAmount != null && Number.isFinite(Number(fin.totalAmount))
        ? Number(fin.totalAmount)
        : 0;
  const paid =
    fin?.paidAmount != null && Number.isFinite(Number(fin.paidAmount))
      ? Number(fin.paidAmount)
      : Number(sumpaidFallback) || 0;
  const remainingA = Math.max(
    0,
    (ilsToAgorot(required) || 0) - (ilsToAgorot(paid) || 0)
  );
  return {
    requiredIls: required,
    paidIls: paid,
    remainingIls: agorotToIls(remainingA) || 0,
    isFullyPaid: Boolean(fin?.isFullyPaid) && remainingA === 0,
    isPartialPaid: Boolean(fin?.isPartialPaid) && remainingA > 0,
  };
}

/**
 * Final confirmation gate: remaining minor units must be 0 (or existing
 * financial SSOT isFullyPaid) AND captured paid >= required.
 */
export function isOrderFullyPaidForConfirmation(order, requiredIls) {
  if (!order || typeof order !== "object") return false;
  const fin = resolveLdsFinancialState(order);
  const remainingA = ilsToAgorot(fin?.remainingBalance ?? 0);
  if (remainingA != null && remainingA > 0) return false;
  if (!fin?.isFullyPaid) return false;
  const requiredA = ilsToAgorot(requiredIls ?? fin.totalAmount);
  const paidA = ilsToAgorot(fin.paidAmount);
  if (requiredA == null || paidA == null) return false;
  return paidA >= requiredA;
}

export function classifyHypIframeHref(href) {
  const url = String(href || "");
  if (!url) return null;
  if (url.includes("/payment/fail/")) return "fail";
  if (url.includes("/payment/cancel/")) return "cancel";
  if (url.includes("/payment/success/") || url.includes("/payment/partial")) {
    return "success";
  }
  return null;
}
